import { FrontendVariables } from "@Front/Config/VariablesFront";
import { rewriteBackApiHostWhenApiSubdomainMirrorsPage } from "@Front/Config/configRuntimeUrlHelpers";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { coalesceThrownApiErrorText } from "@Front/Utils/userFacingMessageFromApiClientError";

/**
 * Module-level state (replaces static properties)
 */
let baseUrl: string | null = null;
let _variables: FrontendVariables | null = null;

export function getVariables(): FrontendVariables {
	if (!_variables) {
		_variables = FrontendVariables.getInstance();
	}
	return _variables;
}

export function invalidateBaseUrl(): void {
	baseUrl = null;
}

export function getBaseUrl(): string {
	if (!baseUrl) {
		baseUrl = buildBackendBaseUrl(getVariables());
	}
	return baseUrl;
}

function getMissingConfigKeys(variables: FrontendVariables): string[] {
	const entries: Array<[string, string | undefined]> = [
		["NEXT_PUBLIC_BACK_API_PROTOCOL", variables.BACK_API_PROTOCOL],
		["NEXT_PUBLIC_BACK_API_HOST", variables.BACK_API_HOST],
		["NEXT_PUBLIC_BACK_API_ROOT_URL", variables.BACK_API_ROOT_URL],
		["NEXT_PUBLIC_BACK_API_VERSION", variables.BACK_API_VERSION],
	];
	return entries.filter(([, v]) => !isValidValue(v)).map(([k]) => k);
}
function validateConfigForRuntime(missingKeys: string[], isSSR: boolean): void {
	if (missingKeys.length === 0) return;
	if (!isSSR) {
		throw new Error(`[BaseApiService] Missing runtime configuration: ${missingKeys.join(", ")}. Ensure NEXT_PUBLIC_* variables are defined before invoking API services.`);
	}
	try {
		LoggerService.warn(`[BaseApiService] Missing build-time configuration: ${missingKeys.join(", ")}. Using defaults for build.`);
	} catch {
		// ignore
	}
}
function buildBackendBaseUrl(variables: FrontendVariables): string {
	const isSSR = typeof window === "undefined";
	const protocol = normalizeProtocol((variables.BACK_API_PROTOCOL || "https://").trim());
	let host = (variables.BACK_API_HOST || "localhost:3001").trim();
	if (!isSSR && typeof window.location?.host === "string") {
		host = rewriteBackApiHostWhenApiSubdomainMirrorsPage(window.location.host, host);
	}
	const rootUrl = ensureLeadingSlash((variables.BACK_API_ROOT_URL || "/api").trim());
	const version = ensureLeadingSlash((variables.BACK_API_VERSION || "/v1").trim());
	validateConfigForRuntime(getMissingConfigKeys(variables), isSSR);
	try {
		return new URL(`${removeTrailingSlash(rootUrl)}${version}`, `${protocol}${host}`).toString().replace(/\/$/, "");
	} catch (error) {
		const reason = coalesceThrownApiErrorText(error);
		LoggerService.error("❌ [BaseApiService] Failed to build backend base URL", { protocol, host, rootUrl, version, error: reason });
		throw new Error(`[BaseApiService] Unable to construct backend base URL. protocol=${protocol}, host=${host}, rootUrl=${rootUrl}, version=${version}`);
	}
}

function isValidValue(value: string | undefined): boolean {
	return typeof value === "string" && value.trim().length > 0;
}

function normalizeProtocol(value: string): string {
	const trimmed = value.trim();
	if (!trimmed.includes("://")) {
		throw new Error(`[BaseApiService] Invalid protocol format: ${value}`);
	}
	return trimmed.endsWith("://") ? trimmed : `${trimmed.split("://")[0]}://`;
}

function ensureLeadingSlash(value: string): string {
	return value.startsWith("/") ? value : `/${value}`;
}

function removeTrailingSlash(value: string): string {
	return value.endsWith("/") ? value.slice(0, -1) : value;
}
